import { useCallback } from "react";
import { trackEvent } from "src/Amplitude";
import {
  addToWatchlistOptimistic,
  removeFromWatchlistOptimistic,
  setWatchlist,
  useUserSlice,
} from "src/store/features/user.slice";
import { useAppDispatch } from "src/store/store";
import {
  useAddWatchlistV2Mutation,
  useRemoveWatchlistV2Mutation,
  useLazyGetWatchlistV2Query,
} from "src/store/features/api/generated";
import type { CurrencyConfigRes } from "src/store/features/api/generated";

interface UseWatchlistOptions {
  /** Optional currency config for optimistic add (if not provided, will use minimal config) */
  currencyConfig?: CurrencyConfigRes;
  /** Optional tracking context (default: "Markets") */
  trackingContext?: string;
}

/**
 * Custom hook for managing watchlist operations with optimistic updates
 * @param options Configuration options for the hook
 * @returns Object with toggle function and helper functions
 */
export const useWatchlist = (options: UseWatchlistOptions = {}) => {
  const dispatch = useAppDispatch();
  const { watchlist } = useUserSlice();
  const [addWatchlistV2] = useAddWatchlistV2Mutation();
  const [removeWatchlistV2] = useRemoveWatchlistV2Mutation();
  const [refetchWatchlist] = useLazyGetWatchlistV2Query();

  const { currencyConfig, trackingContext = "Markets" } = options;

  /**
   * Check if a coin is in the watchlist (case-insensitive)
   */
  const isCoinInWatchlist = useCallback(
    (code: string): boolean => {
      const normalizedCode = code.toUpperCase();
      return (
        watchlist?.some((item) => item.currencyConfig?.code?.toUpperCase() === normalizedCode) ??
        false
      );
    },
    [watchlist],
  );

  /**
   * Toggle watchlist status for a coin (add if not in watchlist, remove if in watchlist)
   * Uses optimistic updates and syncs with server in the background
   */
  const toggleWatchlist = useCallback(
    (code: string, customCurrencyConfig?: CurrencyConfigRes) => {
      const coinCurrencyConfig = customCurrencyConfig || currencyConfig;
      const isFavourite = isCoinInWatchlist(code);

      // Track event
      const trackEventName = isFavourite ? "Removed_Watchlist_Coin" : "Added_Watchlist_Coin";
      trackEvent(trackEventName, { Token_Page: code }, trackingContext);

      // Optimistic update - immediately update Redux (synchronous, happens instantly)
      if (isFavourite) {
        dispatch(removeFromWatchlistOptimistic(code));
      } else {
        // Check if already in watchlist (case-insensitive) - prevent duplicate optimistic adds
        if (isCoinInWatchlist(code)) return;
        dispatch(
          addToWatchlistOptimistic({
            code,
            currencyConfig: coinCurrencyConfig,
          }),
        );
      }

      // Make API call in background (v2 endpoints - fire and forget)
      const performApiCall = async () => {
        try {
          if (isFavourite) {
            // Remove operation
            const result = await removeWatchlistV2({
              removeWatchlistV2Dto: { token: code },
            });

            if ("data" in result && result.data?.success) {
              // Remove successful - optimistic update was correct, no action needed
            } else if ("error" in result) {
              console.error("Watchlist remove error:", result.error);
              // On error, refetch to sync with server state
              refetchWatchlist({ quote: "USD" }).then((result) => {
                if (result.data) {
                  dispatch(setWatchlist(result.data));
                }
              });
            }
          } else {
            // Add operation
            const result = await addWatchlistV2({
              addWatchlistV2Dto: { token: code },
            });

            if ("data" in result && result.data) {
              const response = result.data;
              if (response.success && response.watchlist) {
                // Sync Redux with the full watchlist from server
                dispatch(setWatchlist(response.watchlist));
              } else if (response.error === "Token already in watchlist") {
                // Token is already in watchlist - sync Redux with server
                refetchWatchlist({ quote: "USD" }).then((result) => {
                  if (result.data) {
                    dispatch(setWatchlist(result.data));
                  }
                });
              } else {
                // API call failed - rollback optimistic update
                console.error("Watchlist add error:", response.error);
                // Refetch to ensure we're in sync with server
                refetchWatchlist({ quote: "USD" }).then((result) => {
                  if (result.data) {
                    dispatch(setWatchlist(result.data));
                  }
                });
              }
            } else if ("error" in result) {
              console.error("Watchlist add error:", result.error);
              // On error, refetch to sync with server state
              refetchWatchlist({ quote: "USD" }).then((result) => {
                if (result.data) {
                  dispatch(setWatchlist(result.data));
                }
              });
            }
          }
        } catch (error) {
          // API call failed - rollback optimistic update
          console.error("Watchlist API error:", error);
          refetchWatchlist({ quote: "USD" }).then((result) => {
            if (result.data) {
              dispatch(setWatchlist(result.data));
            }
          });
        }
      };

      // Fire and forget - don't await, let it run in background
      performApiCall();
    },
    [
      isCoinInWatchlist,
      currencyConfig,
      dispatch,
      addWatchlistV2,
      removeWatchlistV2,
      refetchWatchlist,
      trackingContext,
    ],
  );

  return {
    toggleWatchlist,
    isCoinInWatchlist,
    watchlist,
  };
};
